import * as React from 'react';

import URI from 'jsuri';
import { isEmpty } from 'lodash';
import Q from 'q';

import API from 'js/lib/api';
import { FormattedHTMLMessage } from 'js/lib/coursera.react-intl';

import { Notification } from '@coursera/coursera-ui';

import { IMPORT_CURRICULUM_TEMPLATE_URL } from 'bundles/enterprise-admin-constants/ProgramMembersConstants';
import S3Upload from 'bundles/enterprise-admin-program-users/utils/s3uploader';
import CSVPreUploadPreview from 'bundles/enterprise-admin-users/components/csvImportWidget/CSVPreUploadPreviewV2';
import FileUploadWidget from 'bundles/enterprise-admin-users/components/csvImportWidget/FileUploadWidgetV2';
import Button from 'bundles/internal-ui/components/buttons/Button';
import Card from 'bundles/internal-ui/components/cards/Card';
import TextBox from 'bundles/internal-ui/components/forms/TextBox';

import _t from 'i18n!nls/internal-enterprise';

type State = {
  defaultFileMessage: string;
  errorMessage: string | null;
  successMessage: string | null;
  file: File | null;
  slug: string;
};

const programsAPI = API('/api/enterprisePrograms.v1', { type: 'rest' });
const programCurriculumAPI = API('/api/programCurriculumFileOperations.v1', { type: 'rest' });

class EnterpriseProgramBulkAdd extends React.Component<{}, State> {
  state: State = {
    defaultFileMessage: _t('You can upload 1 file at a time'),
    errorMessage: null,
    successMessage: null,
    file: null,
    slug: '',
  };

  handleUpdateText = (event: React.ChangeEvent<any>) => {
    this.setState({ slug: event.target.value });
  };

  handleFilesDropped = ({ files }: any) => {
    if (!isEmpty(files)) {
      this.setState({
        file: files[0],
        defaultFileMessage: _t('Will replace current file'),
      });
    }
  };

  handleDeletingFile = () => {
    this.setState({
      file: null,
      defaultFileMessage: _t('You can upload 1 file at a time'),
    });
  };

  handleUpload = () => {
    const { slug, file } = this.state;
    if (!file) {
      this.setState({ errorMessage: _t('Please upload a file to continue.'), successMessage: null });
      return;
    }

    if (!slug) {
      this.setState({ errorMessage: _t('Please provide a program slug to continue.'), successMessage: null });
      return;
    }

    const uri = new URI().addQueryParam('q', 'slug').addQueryParam('slug', slug);
    Q(programsAPI.get(uri.toString()))
      .then((res) => {
        const programId = (((res || {}).elements || [])[0] || {}).id;
        this.uploadToS3(programId);
      })
      .catch((err) => {
        let errorMessage = '';
        if (err.status === 404) {
          errorMessage = _t('Program not found');
        } else {
          errorMessage = err.responseJSON.msg || err.responseJSON.message;
        }
        this.setState({ errorMessage, successMessage: null });
      });
  };

  onInputKeyPress = (event: React.KeyboardEvent<any>) => {
    if (event.key === 'Enter') {
      this.handleUpload();
    }
  };

  getUploadLocation = (programId: string, callback: (res: any) => void) => {
    const uri = new URI().addQueryParam('action', 'generateUploadLocation').addQueryParam('programId', programId);
    return Q(programCurriculumAPI.post(uri.toString()))
      .then((res) => {
        callback(res);
      })
      .catch((err) => {
        let errorMessage = '';
        if (err.status === 404) {
          errorMessage = _t('Generating S3 location failed.');
        } else {
          errorMessage = err.responseJSON.msg || err.responseJSON.message;
        }
        this.setState({ errorMessage });
      });
  };

  uploadToS3 = (programId: string) => {
    const { file } = this.state;
    const uploader = new S3Upload({
      getSignedUrl: this.getUploadLocation.bind(this, programId),
      onFinishS3Put: this.importProgramCurriculum.bind(this, programId),
    });
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    return uploader.handleFileSelect(file!);
  };

  importProgramCurriculum = (programId: string, s3Object: any) => {
    const file = s3Object.s3Location;
    const uri = new URI().addQueryParam('action', 'processImport');
    const body = {
      file,
      programId,
    };
    Q(programCurriculumAPI.post(uri.toString(), { data: body }))
      .then((res) => {
        this.setState({
          successMessage: _t('Successfully imported curriculum to programId: #{programId}', { programId }),
          errorMessage: null,
          slug: '',
        });
        this.handleDeletingFile();
      })
      .catch((err) => {
        let errorMessage = '';
        if (err.status === 404) {
          errorMessage = _t('Importing curriculum failed.');
        } else {
          errorMessage = err.responseJSON.msg || err.responseJSON.message;
        }
        this.setState({ errorMessage, successMessage: null });
      });
  };

  render() {
    const { defaultFileMessage, errorMessage, successMessage, file, slug } = this.state;
    return (
      <div className="rc-EnterpriseToolsHome">
        <Card>
          <h3>{_t('Bulk add courses and specializations to a program')}</h3>
          <FormattedHTMLMessage
            message={_t(
              `
              <p class="download-csv-link"><span><a class="download-csv-link" href={csvTemplateLink} target="_blank">Download the CSV Template </a></span>
              to add courses and specializations to a program. <b>Note: This will overwrite the current curriculum for the program.</b></p>
            `
            )}
            csvTemplateLink={IMPORT_CURRICULUM_TEMPLATE_URL}
          />
          <TextBox
            placeholder={_t('Program Slug')}
            onUpdateField={this.handleUpdateText}
            currentValue={slug}
            inputProps={{ onKeyPress: this.onInputKeyPress, autoFocus: true }}
          />
          <br />
          <FormattedHTMLMessage
            message={_t(
              `
              <p><strong>Note: Selected For Credit, Session Start Date, Session End Date</strong> and <strong>Session Name</strong> are optional fields and can be configured later in the enterprise admin configuration panel. If you choose to configure them now, please wait for a few minutes for the changes to be reflected after the upload completes.</p>
            `
            )}
          />
          <br />
          <FileUploadWidget
            onDrop={this.handleFilesDropped}
            accept={{ 'text/csv': ['.csv'] }}
            singleFileOnly={true}
            defaultFileMessage={defaultFileMessage}
          >
            {file && <CSVPreUploadPreview files={[file]} onDelete={this.handleDeletingFile} />}
          </FileUploadWidget>
          <br />
          <Button text={_t('Upload Curriculum')} btnStyle="info" onClick={this.handleUpload} />
          <div>
            <br />
            {successMessage && (
              <Notification
                message={successMessage}
                type="success"
                style={{ 'box-sizing': 'border-box', 'font-size': '14px' }}
              />
            )}
            {errorMessage && (
              <Notification
                message={errorMessage}
                type="error"
                style={{ 'box-sizing': 'border-box', 'font-size': '14px' }}
              />
            )}
          </div>
        </Card>
      </div>
    );
  }
}

export default EnterpriseProgramBulkAdd;
